import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import { ShortsCarousel } from '@quintype/arrow';
import { ShortsAdWidget } from './shorts-ad-widget';

import './shorts.m.css';

export const ShortsPage = ({ data = {}, config = {} }) => {
  const enableDarkMode = useSelector(state => get(state, ['header', 'isDarkModeEnabled'], false));
  const shortsPrefix = (get(config, ['pagebuilder-config', 'featureToggle', 'configureShortsRoute'], 'shorts') || 'shorts').replace(/^\//, '');
  const returnUrlRef = useRef(null);

  const shortsEnabled = get(config, ['pagebuilder-config', 'featureToggle', 'shortsPage'], false);
  const { stories = [], startIndex = 0, entryStory, collectionSlug, childCollectionsWithMore = [], initialTopLevelOffset } = data;

  const widgetCode = get(config, ['pagebuilder-config', 'shorts', 'widgetCode'], null);
  const targetingId = get(config, ['pagebuilder-config', 'shorts', 'targetingId'], null);
  const mobilePlaceholderHeight = Math.min(Number(get(config, ['pagebuilder-config', 'shorts', 'mobilePlaceholderHeight'], '')), 600);
  const desktopPlaceholderHeight = Math.min(Number(get(config, ['pagebuilder-config', 'shorts', 'desktopPlaceholderHeight'], '')), 600);

  const adConfig = useMemo(() => {
    return widgetCode
      ? {
        enabled: true,
        adComponent: () => (
          <ShortsAdWidget
            widgetCode={widgetCode}
            targetingId={targetingId}
            mobilePlaceholderHeight={mobilePlaceholderHeight}
            desktopPlaceholderHeight={desktopPlaceholderHeight}
            enableDarkMode={enableDarkMode}
          />
        )
      }
      : { enabled: false };
  }, [widgetCode, targetingId, mobilePlaceholderHeight, desktopPlaceholderHeight, enableDarkMode]);

  const handleStoryChange = useCallback((story) => {
    if (typeof window === 'undefined') return;
    window.history.replaceState(null, story.headline, `/${shortsPrefix}/${story.slug}`);
  }, [shortsPrefix]);

  const handleClose = useCallback(() => {
    window.location.href = returnUrlRef.current || '/';
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const storedReturnUrl = window.sessionStorage.getItem('shortsReturnUrl');
    if (storedReturnUrl) {
      returnUrlRef.current = storedReturnUrl;
      window.sessionStorage.removeItem('shortsReturnUrl');
      return;
    }

    const referrer = document.referrer;
    if (referrer) {
      try {
        const referrerUrl = new URL(referrer);
        const isSameOrigin = referrerUrl.origin === window.location.origin;
        const isShorts = referrerUrl.pathname.startsWith(`/${shortsPrefix}`);
        if (isSameOrigin && !isShorts) {
          returnUrlRef.current = referrerUrl.pathname + referrerUrl.search + referrerUrl.hash;
        }
      } catch (err) {
        console.error("Failed to parse referrer URL in Shorts component:", err);
      }
    }
  }, []);

  if (!shortsEnabled) return null;
  if (!stories.length) return null;

  const logo = get(config, ['pagebuilder-config', 'header', 'logo'], '');
  const darkLogo = get(config, ['pagebuilder-config', 'header', 'darkLogo'], logo);
  const logoAltText = get(config, ['pagebuilder-config', 'general', 'logoAltText'], '') || get(config, ['publisher-name'], '');
  const activeLogo = enableDarkMode ? darkLogo : logo;
  const readFullStoryButton = get(config, ['pagebuilder-config', 'shorts', 'readFullStoryButton'], 'Read Full Story');
  const allCaughtUpTitle = get(config, ['pagebuilder-config', 'shorts', 'allCaughtUpText']) || 'You are all caught up';
  const allCaughtUpMessage = get(config, ['pagebuilder-config', 'shorts', 'allStoriesReadText']) || "That's all for now. More stories will appear here soon.";
  const timeSpentLabel = get(config, ['pagebuilder-config', 'shorts', 'timeSpentText']) || 'Time Spent';
  const storiesReadLabel = get(config, ['pagebuilder-config', 'shorts', 'storiesReadText']) || 'Stories Read';

  const pbConfig = get(config, ['pagebuilder-config'], {});
  const { primaryColor = '#4860BC', darkPrimaryColor = primaryColor } = get(pbConfig, ['general'], {});
  const activePrimaryColor = enableDarkMode ? darkPrimaryColor : primaryColor;

  return (
    <div
      styleName={`shorts-page-wrapper${enableDarkMode ? ' shorts-page-wrapper--dark' : ''}`}
      style={activePrimaryColor ? { '--primaryColor': activePrimaryColor } : undefined}
    >
      <div styleName={`shorts-navbar${enableDarkMode ? ' shorts-navbar--dark' : ''}`}>
        
        <div styleName="shorts-logo-container">
          <span aria-hidden="true" />
          <div styleName="shorts-logo-wrapper">
            {activeLogo && (
              <img
                className="logoHeight"
                src={activeLogo}
                alt={logoAltText}
              />
            )}
          </div>
          <span>
            <div styleName="shorts-close-wrapper">
          <button styleName="shorts-close-btn" onClick={handleClose} aria-label="Close shorts">
            <svg styleName="shorts-close-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 45 45" fill="none">
              <rect width="44" height="44" rx="22.4" fill="var(--primaryColor)" />
              <path d="M16 16L28.8 28.8M28.8 16L16 28.8" stroke="white" strokeWidth="2.4" strokeLinecap="round" />
            </svg>
          </button>
        </div>
            </span>
        </div>
      </div>
      <ShortsCarousel
        stories={stories}
        startIndex={startIndex}
        entryStory={entryStory}
        config={config}
        shortsPrefix={shortsPrefix}
        readFullStoryButton={readFullStoryButton}
        enableDarkMode={enableDarkMode}
        onStoryChange={handleStoryChange}
        adConfig={adConfig}
        allCaughtUpTitle={allCaughtUpTitle}
        allCaughtUpMessage={allCaughtUpMessage}
        timeSpentLabel={timeSpentLabel}
        storiesReadLabel={storiesReadLabel}
        collectionSlug={collectionSlug}
        childCollectionsWithMore={childCollectionsWithMore}
        initialTopLevelOffset={initialTopLevelOffset}
      />
    </div>
  );
};
